import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  CreateSample,
  PlanStatusEnum,
  ResultFilterEnum,
  SamplePage,
  SampleStatusEnum,
  type UpdateSample
} from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

// Helper function to extract first planId from a list. Throws an error if more than one element is provided.
const getFirstPlanIdFromList = (planIds?: number[]): number | undefined => {
  if (!planIds || planIds.length === 0) return undefined
  if (planIds.length === 1) return planIds[0]
  throw new Error('Filtrering på flere planId-er støttes ikke.')
}

interface CreateSampleMutationVariables {
  sampleId: string
  body: CreateSample
}

interface UpdateSampleMutationVariables {
  sampleId: string
  etag: string
  body: UpdateSample
}

export interface GetSamplesQueryParams {
  isEnabled: boolean
  includeEvents?: boolean
  ownerScope?: 'exact' | 'subtree'
  userId?: string
  sampleTypeId?: number
  planIds?: number[]
  okProgramId?: number
  taskIds?: number[]
  ownerIds?: string[]
  substanceIds?: number[]
  matrixIds?: number[]
  productIds?: number[]
  sampntTypeIds?: number[]
  sampleIds?: string[]
  producerSearch?: string
  status?: SampleStatusEnum[]
  result?: ResultFilterEnum
  planStatuses?: PlanStatusEnum[]
  fromDate?: string
  toDate?: string
  page?: number
  size?: number
  sort?: string
}

export type GetSamplesInfiniteQueryParams = GetSamplesQueryParams

const ACCEPT_V3_HEADER = {
  Accept: 'application/vnd.mattilsynet.proveta.v3+json'
}

const getOptionalList = <T>(list?: T[]): T[] | undefined => {
  return list != null && list.length > 0 ? list : undefined
}

const buildSamplesQuery = (params: GetSamplesQueryParams, page?: number) => {
  const planId = getFirstPlanIdFromList(params.planIds)

  return {
    ownerScope: params.ownerScope,
    userId: params.userId,
    sampleTypeId: params.sampleTypeId,
    planId,
    okProgramId: params.okProgramId,
    taskIds: getOptionalList(params.taskIds),
    ownerIds: getOptionalList(params.ownerIds),
    substanceIds: getOptionalList(params.substanceIds),
    matrixIds: getOptionalList(params.matrixIds),
    productIds: getOptionalList(params.productIds),
    sampntTypeIds: getOptionalList(params.sampntTypeIds),
    sampleIds: getOptionalList(params.sampleIds),
    producerSearch: params.producerSearch,
    status: params.status,
    planStatuses: params.planStatuses,
    conclusion: params.result === 'CANCELLED' ? undefined : params.result,
    compromised: params.result === 'CANCELLED' ? true : undefined,
    includeEvents: params.includeEvents,
    fromDate: params.fromDate || undefined,
    toDate: params.toDate || undefined,
    page,
    size: params.size,
    sort: params.sort
  }
}

const fetchSamples = async (
  authFetchV3: ReturnType<typeof useAuth>['authFetchV3'],
  params: GetSamplesQueryParams,
  page?: number
) => {
  const { data, error } = await authFetchV3().GET('/samples', {
    headers: ACCEPT_V3_HEADER,
    params: {
      query: buildSamplesQuery(params, page)
    }
  })

  if (error) {
    throwOnError(error)
  }

  return data
}

export const useCreateSampleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({ sampleId, body }: CreateSampleMutationVariables) => {
      const { data, error } = await authFetchV3().PUT('/samples/{id}', {
        params: {
          path: { id: sampleId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(['samples', data.id], data)
      await queryClient.invalidateQueries({
        queryKey: ['samples']
      })
      await queryClient.invalidateQueries({
        queryKey: ['tasks', data.taskId]
      })
    }
  })
}

export const useGetSamplesQuery = (params: GetSamplesQueryParams) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['samples', params],
    queryFn: params.isEnabled
      ? async () => fetchSamples(authFetchV3, params, params.page)
      : skipToken,
    placeholderData: keepPreviousData
  })
}

export const useGetSamplesInfiniteQuery = (
  params: GetSamplesInfiniteQueryParams
) => {
  const { authFetchV3 } = useAuth()
  return useInfiniteQuery({
    queryKey: ['samples', params],
    initialPageParam: 0,
    getNextPageParam: (lastPage: SamplePage, allPages, lastPageParam) => {
      const nextPage = lastPageParam + 1
      if (lastPage.totalPages != null && lastPage.totalPages <= nextPage) {
        return undefined
      }
      return nextPage
    },
    queryFn: params.isEnabled
      ? async (ctx) => fetchSamples(authFetchV3, params, ctx.pageParam)
      : skipToken
  })
}

export const useGetSampleByIdQuery = (
  sampleId?: string,
  skipIfNew?: boolean
) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['samples', sampleId],
    queryFn:
      sampleId != null && sampleId.length > 0 && !skipIfNew
        ? async () => {
            const { data, error } = await authFetchV3().GET('/samples/{id}', {
              headers: {
                Accept: 'application/vnd.mattilsynet.proveta.v3+json'
              },
              params: {
                path: {
                  id: sampleId
                }
              }
            })
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

export const useGetSampleHistoryQuery = (sampleId?: string) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['samples', sampleId, 'history'],
    queryFn:
      sampleId != null && sampleId.length > 0
        ? async () => {
            const { data, error } = await authFetchV3().GET(
              '/samples/{id}/history',
              {
                headers: {
                  Accept: 'application/vnd.mattilsynet.proveta.v3+json'
                },
                params: {
                  path: {
                    id: sampleId
                  }
                }
              }
            )
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

export const useUpdateSampleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      sampleId,
      etag,
      body
    }: UpdateSampleMutationVariables) => {
      const { data, error } = await authFetchV3().PATCH('/samples/{id}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { id: sampleId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['samples', data.id]
      })
    }
  })
}

export const useDeleteSampleMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      sampleId,
      etag
    }: {
      sampleId: string
      etag: string
    }) =>
      await authFetchV3().DELETE('/samples/{id}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { id: sampleId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['samples']
      })
    }
  })
}

export const useBulkUpdateSampleStatus = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      sampleIds,
      newStatus
    }: {
      sampleIds: string[]
      newStatus: string
    }) => {
      const { data, error } = await authFetchV3().PATCH('/samples/status', {
        body: { sampleIds, newStatus }
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (data) => {
      if (Array.isArray(data?.statuses)) {
        for (const status of data.statuses.filter(
          (d) => !d.status.startsWith('Failed')
        )) {
          await queryClient.invalidateQueries({
            queryKey: ['samples', status.sampleId]
          })
        }
      }
    }
  })
}

export const useGetSampleInspectorCandidatesQuery = (sampleId?: string) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['samples', sampleId, 'inspector-candidates'],
    queryFn:
      sampleId != null && sampleId.length > 0
        ? async () => {
            const { data, error } = await authFetchV3().GET(
              '/samples/{id}/inspector-candidates',
              {
                headers: {
                  Accept: 'application/json'
                },
                params: {
                  path: {
                    id: sampleId
                  }
                }
              }
            )
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

export type UpdateSampleInspectorMutationVariables = {
  sampleId: string
  etag: string
  userId: string
}

export const useUpdateSampleInspectorMutation = () => {
  const queryClient = useQueryClient()
  const { authFetchV3 } = useAuth()
  return useMutation({
    mutationFn: async ({
      sampleId,
      etag,
      userId
    }: UpdateSampleInspectorMutationVariables) => {
      const { data, error } = await authFetchV3().PATCH(
        '/samples/{id}/inspector',
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            header: {
              'If-Match': etag
            },
            path: { id: sampleId }
          },
          body: { userId }
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['samples', variables.sampleId]
        }),
        queryClient.invalidateQueries({
          queryKey: ['samples', variables.sampleId, 'history']
        }),
        queryClient.invalidateQueries({
          queryKey: ['samples', variables.sampleId, 'inspector-candidates']
        }),
        queryClient.invalidateQueries({
          queryKey: ['samples']
        }),
        queryClient.invalidateQueries({
          queryKey: ['requisitions']
        })
      ])
    }
  })
}
